import { useState, useEffect, useRef } from 'react'
import { X, Copy, Share2, Check, RefreshCw } from 'lucide-react'
import { useRegenerateSurvivorInviteCode } from '../queries'
import type { SurvivorGameDetail } from '../types'
import { Portal } from '@/shared/components/ui/Portal'
import styles from './SurvivorSheets.module.css'

type Props = {
  game:    SurvivorGameDetail
  onClose: () => void
}

export function SurvivorInviteSheet({ game, onClose }: Props) {
  const [copied, setCopied] = useState(false)
  const sheetRef = useRef<HTMLDivElement>(null)
  const regenerate = useRegenerateSurvivorInviteCode(game.id)

  const joinCode = game.joinCode
  const shareUrl = `https://fantyx.pro/entrar?code=${joinCode}`

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  useEffect(() => {
    sheetRef.current?.focus()
  }, [])

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(joinCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* clipboard unavailable */ }
  }

  async function handleShare() {
    if (navigator.share) {
      await navigator.share({
        title: `Entre no jogo: ${game.name}`,
        text: `Use o código ${joinCode} para entrar no Sobrevivente "${game.name}" no Fantyx.Pro!`,
        url: shareUrl,
      })
    } else {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <Portal>
      <div className={styles.backdrop} onClick={onClose} role="dialog" aria-modal="true" aria-label="Convidar participantes">
        <div ref={sheetRef} className={styles.sheet} onClick={(e) => e.stopPropagation()} tabIndex={-1}>
          <div className={styles.header}>
            <span className={styles.title}>Convidar participantes</span>
            <button className={styles.closeBtn} onClick={onClose} aria-label="Fechar">
              <X size={16} />
            </button>
          </div>

          <div className={styles.body}>
            <p className={styles.hint}>Compartilhe o código abaixo com quem você quer convidar.</p>

            <div className={styles.codeBlock}>
              <span className={styles.code}>{joinCode}</span>
              <button className={styles.copyBtn} onClick={handleCopy} aria-label="Copiar código">
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? 'Copiado!' : 'Copiar'}
              </button>
            </div>

            <button className={styles.shareBtn} onClick={handleShare}>
              <Share2 size={16} />
              Compartilhar convite
            </button>

            <p className={styles.membersCount}>
              {game.participantCount} de {game.maxParticipants} participantes
            </p>

            {game.isOwner && (
              <button
                className={styles.regenerateLink}
                onClick={() => regenerate.mutate()}
                disabled={regenerate.isPending}
              >
                <RefreshCw size={12} aria-hidden="true" />{' '}
                {regenerate.isPending ? 'Gerando…' : 'Gerar novo código'}
              </button>
            )}
          </div>
        </div>
      </div>
    </Portal>
  )
}
